/** Shared 52W return sparkline helpers (header + Stories cards). */

export const SPARK_GREEN = '#4ade80';
/** Benchmark / neutral series — keep true yellow, not brand Cool Porcelain. */
export const SPARK_YELLOW = '#ffd21f';
export const SPARK_RED = '#f87171';

const SPARK_EPS = 1e-9;

type SparkDataPoint = { x: number; y: number; r: number; b: number };

export function computeReturns(prices: { week_date: string; price_close: number }[]): number[] | null {
  if (prices.length < 2) return null;
  const base = prices[0].price_close;
  if (base <= 0) return null;
  return prices.map((p) => ((p.price_close / base) - 1) * 100);
}

export function computeReturnsFromCloses(closes: number[]): number[] | null {
  if (closes.length < 2) return null;
  const base = closes[0];
  if (base <= 0) return null;
  return closes.map((p) => ((p / base) - 1) * 100);
}

/** Match benchmark closes to each stock week (forward-fill missing weeks). */
export function alignBenchmarkCloses(
  stockPrices: { week_date: string; price_close: number }[],
  benchmark: { week_date: string; price_close: number }[],
): number[] | null {
  if (!benchmark.length || !stockPrices.length) return null;

  const benchByDate = new Map(benchmark.map((b) => [b.week_date, b.price_close]));
  let lastKnown: number | null = null;
  const aligned: number[] = [];

  for (const p of stockPrices) {
    const close = benchByDate.get(p.week_date);
    if (close != null) {
      lastKnown = close;
      aligned.push(close);
    } else if (lastKnown != null) {
      aligned.push(lastKnown);
    } else {
      return null;
    }
  }

  return aligned;
}

export function sparkZoneColor(r: number, b: number): string {
  if (r >= 0 && r >= b) return SPARK_GREEN;
  if (r < 0 && r < b) return SPARK_RED;
  return SPARK_YELLOW;
}

function interpolateSparkPoint(p0: SparkDataPoint, p1: SparkDataPoint, t: number): SparkDataPoint {
  return {
    x: p0.x + t * (p1.x - p0.x),
    y: p0.y + t * (p1.y - p0.y),
    r: p0.r + t * (p1.r - p0.r),
    b: p0.b + t * (p1.b - p0.b),
  };
}

function sparkCrossings(p0: SparkDataPoint, p1: SparkDataPoint): number[] {
  const ts: number[] = [];

  const dr = p1.r - p0.r;
  if (Math.abs(dr) > SPARK_EPS) {
    const t = -p0.r / dr;
    if (t > SPARK_EPS && t < 1 - SPARK_EPS) ts.push(t);
  }

  const f0 = p0.r - p0.b;
  const df = (p1.r - p0.r) - (p1.b - p0.b);
  if (Math.abs(df) > SPARK_EPS) {
    const t = -f0 / df;
    if (t > SPARK_EPS && t < 1 - SPARK_EPS) ts.push(t);
  }

  ts.sort((a, b) => a - b);
  const unique: number[] = [];
  for (const t of ts) {
    if (!unique.length || t - unique[unique.length - 1] > SPARK_EPS) unique.push(t);
  }
  return unique;
}

/** Catmull-Rom → cubic bezier for soft corners between weekly points. */
export function smoothSparkPath(points: { x: number; y: number }[], tension = 0.42): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`;
  if (points.length === 2) {
    return `M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)} L${points[1].x.toFixed(1)},${points[1].y.toFixed(1)}`;
  }

  let d = `M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    const cp1x = p1.x + ((p2.x - p0.x) * tension) / 6;
    const cp1y = p1.y + ((p2.y - p0.y) * tension) / 6;
    const cp2x = p2.x - ((p3.x - p1.x) * tension) / 6;
    const cp2y = p2.y - ((p3.y - p1.y) * tension) / 6;
    d += ` C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
  }
  return d;
}

export function buildSparkColoredSegments(
  stockReturns: number[],
  stockPoints: { x: number; y: number }[],
  benchReturns: number[] | null,
  /** false: straight segments (a large weekly chart, where a spline invents peaks). */
  smooth = true,
): { path: string; color: string }[] {
  const pieces: { points: { x: number; y: number }[]; color: string }[] = [];

  for (let i = 0; i < stockPoints.length - 1; i++) {
    const p0: SparkDataPoint = {
      ...stockPoints[i],
      r: stockReturns[i],
      b: benchReturns?.[i] ?? 0,
    };
    const p1: SparkDataPoint = {
      ...stockPoints[i + 1],
      r: stockReturns[i + 1],
      b: benchReturns?.[i + 1] ?? 0,
    };

    const ts = [0, ...sparkCrossings(p0, p1), 1];

    for (let j = 0; j < ts.length - 1; j++) {
      const ta = ts[j];
      const tb = ts[j + 1];
      if (tb - ta < SPARK_EPS) continue;

      const tm = (ta + tb) / 2;
      const color = sparkZoneColor(
        p0.r + tm * (p1.r - p0.r),
        p0.b + tm * (p1.b - p0.b),
      );
      const a = ta === 0 ? p0 : interpolateSparkPoint(p0, p1, ta);
      const bPt = tb === 1 ? p1 : interpolateSparkPoint(p0, p1, tb);

      const last = pieces[pieces.length - 1];
      const pt = { x: bPt.x, y: bPt.y };
      if (last && last.color === color) {
        last.points.push(pt);
      } else {
        pieces.push({
          points: [{ x: a.x, y: a.y }, pt],
          color,
        });
      }
    }
  }

  return pieces.map((seg) => ({
    color: seg.color,
    path: smooth
      ? smoothSparkPath(seg.points)
      : seg.points.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' '),
  }));
}

export function sparkTotalColor(totalReturn: number, benchTotal: number | null): string {
  const benchT = benchTotal ?? 0;
  if (totalReturn >= 0 && totalReturn >= benchT) return SPARK_GREEN;
  if (totalReturn < 0 && totalReturn < benchT) return SPARK_RED;
  return SPARK_YELLOW;
}
